import { TuneRounded } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, ListItemText, Menu, MenuItem } from '@mui/material';
import { FC, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';

import { DISPLAY_KIND_OPTIONS } from './mapDisplayKinds';

/**
 * leaflet のペイン (z-index 400) とコントロール (同 1000) より手前。
 * 後ろに置くとクリックが地図に吸われる。
 * 詳細パネル (DetailPanelShell の mapTopRight = 1100) より手前でもある
 * （パネルは top: 44 で下にずらしてあるので重ならないが、順序は明示しておく）。
 */
const OVERLAY_Z_INDEX = 1200;

type Props = {
  displayKinds: string[];
  onDisplayKindsChange: (next: string[]) => void;
  /** 運行状況の表示。未指定なら項目ごと出さない（車両位置のライセンスが無い会社） */
  showTruckStatus?: boolean;
  onShowTruckStatusChange?: (next: boolean) => void;
};

/**
 * 地図の表示フィルタ。ヘッダのツールバーではなく地図の右上に重ねる
 * （見ている地図の上で切り替えられるようにするため。左上は leaflet の
 * ズームコントロールが使っている）。
 *
 * 運行状況の ON/OFF も同じメニューに入れる。どちらも「地図に何を出すか」の設定で、
 * 別々の場所にあると探す場所が 2 つになる。
 */
export const MapDisplayFilter: FC<Props> = ({
  displayKinds, onDisplayKindsChange, showTruckStatus, onShowTruckStatusChange,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const toggleKind = (option: string) => {
    const next = displayKinds.includes(option)
      ? displayKinds.filter((it) => it !== option)
      // 元の並び (DISPLAY_KIND_OPTIONS) を保つ。押した順に並べると保存値が毎回変わる
      : DISPLAY_KIND_OPTIONS.filter((it) => it === option || displayKinds.includes(it));
    ReactGA.event('click', {
      screen_name: SCREEN_NAMES.PLANNING_MAP,
      button_name: `地図表示 ${option}`,
      label: next.includes(option) ? 'ON' : 'OFF',
    });
    onDisplayKindsChange(next);
  };

  return (
    <Box
      data-testid="map-display-filter"
      // sx ではなく style。jsdom は sx を計算しないので、置き場所をテストで固定できない
      style={{ position: 'absolute', top: 8, right: 16, zIndex: OVERLAY_Z_INDEX }}
    >
      <Button
        size="small"
        variant="contained"
        color="inherit"
        onClick={(e) => {
          ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING_MAP, button_name: '表示フィルタ' });
          setAnchor(e.currentTarget);
        }}
        startIcon={<TuneRounded sx={{ fontSize: 14 }} />}
        aria-label="地図の表示フィルタ"
        sx={{
          height: 28,
          minWidth: 0,
          px: 1,
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'none',
          bgcolor: 'background.paper',
          color: 'text.secondary',
          boxShadow: 2,
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        表示フィルタ
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {DISPLAY_KIND_OPTIONS.map((option) => (
          <MenuItem key={option} onClick={() => toggleKind(option)} sx={{ py: 0.25 }}>
            <Checkbox
              size="small"
              edge="start"
              checked={displayKinds.includes(option)}
              tabIndex={-1}
              disableRipple
              slotProps={{ input: { 'aria-label': option } }}
              sx={{ p: 0.5 }}
            />
            <ListItemText primary={option} slotProps={{ primary: { fontSize: 12 } }} />
          </MenuItem>
        ))}
        {onShowTruckStatusChange && <Divider sx={{ my: 0.5 }} />}
        {onShowTruckStatusChange && (
          <MenuItem
            onClick={() => {
              const next = !showTruckStatus;
              ReactGA.event('click', {
                screen_name: SCREEN_NAMES.PLANNING_MAP,
                button_name: '運行状況表示',
                label: next ? 'ON' : 'OFF',
              });
              onShowTruckStatusChange(next);
            }}
            sx={{ py: 0.25 }}
          >
            <Checkbox
              size="small"
              edge="start"
              checked={!!showTruckStatus}
              tabIndex={-1}
              disableRipple
              slotProps={{ input: { 'aria-label': '運行状況' } }}
              sx={{ p: 0.5 }}
            />
            <ListItemText primary="運行状況" slotProps={{ primary: { fontSize: 12 } }} />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
